import { useEffect, useRef, useState } from "react";
import { Pointer } from "lucide-react";

const DURATION = 9.5; // seconds for the full journey
const TRAIL_COUNT = 14;
const SVG_NS = "http://www.w3.org/2000/svg";

// Smooth Catmull-Rom spline (as cubic beziers) through the waypoints.
function splinePath(pts) {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

// Distance fraction (0..100) of each click waypoint along the path.
function clickFractions(d, clicks) {
  const svg = document.createElementNS(SVG_NS, "svg");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", d);
  svg.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;left:-9999px;top:-9999px;";
  svg.appendChild(path);
  document.body.appendChild(svg);
  const total = path.getTotalLength();
  const N = 360;
  const samples = [];
  for (let i = 0; i <= N; i++) {
    const pt = path.getPointAtLength((total * i) / N);
    samples.push({ f: (i / N) * 100, x: pt.x, y: pt.y });
  }
  document.body.removeChild(svg);
  return clicks.map((c) => {
    let best = 0, bd = Infinity;
    for (const s of samples) {
      const dx = s.x - c.x, dy = s.y - c.y, dd = dx * dx + dy * dy;
      if (dd < bd) { bd = dd; best = s.f; }
    }
    return best;
  });
}

// Keyframes with ease-in-out per segment + a dwell (pause) at each click point.
function buildKeyframes(name, clickPcts) {
  const [c1, c2, c3] = clickPcts;
  const dwell = 0.045;
  const travel = 1 - dwell * 3;
  let t = 0;
  const rows = [];
  const add = (time, dist) => rows.push({ t: Math.min(1, time), d: dist });
  add(0, 0);
  t += travel * ((c1 - 0) / 100); add(t, c1);
  t += dwell; add(t, c1);
  t += travel * ((c2 - c1) / 100); add(t, c2);
  t += dwell; add(t, c2);
  t += travel * ((c3 - c2) / 100); add(t, c3);
  t += dwell; add(t, c3);
  add(1, 100);

  const body = rows
    .map((r, i) => {
      const pct = (r.t * 100).toFixed(2);
      const timing = i < rows.length - 1 ? " animation-timing-function: ease-in-out;" : "";
      return `${pct}% { offset-distance: ${r.d.toFixed(2)}%;${timing} }`;
    })
    .join("\n");
  return `@keyframes ${name} {\n${body}\n}`;
}

export const HandGuide = () => {
  const layerRef = useRef(null);
  const [anim, setAnim] = useState(null); // { d, name, css, trails }
  const playing = useRef(false);
  const armed = useRef(true);

  useEffect(() => {
    let raf;
    const check = () => {
      raf = requestAnimationFrame(check);
      if (playing.current || !layerRef.current) return;

      const grid = document.querySelector('[data-testid="social-grid"]');
      const logos = document.querySelector('[data-testid="social-logos-row"]');
      const twitch = document.querySelector('[data-testid="social-card-twitch"]');
      const tiktok = document.querySelector('[data-testid="social-card-tiktok"]');
      const logoClick = document.querySelector('[data-testid="social-logo-discord"]');
      if (!grid || !logos || !twitch || !tiktok || !logoClick) return;

      const g = grid.getBoundingClientRect();
      const l = logos.getBoundingClientRect();
      const vh = window.innerHeight;
      const allVisible = g.top >= -6 && l.bottom <= vh + 6 && l.top < vh;

      if (!allVisible) { armed.current = true; return; }
      if (!armed.current) return;

      armed.current = false;
      playing.current = true;

      // Coordinates are RELATIVE to this layer (which sits inside the socials
      // section), so the whole path stays glued to the boxes/logos while the
      // user scrolls — independent of the viewport position.
      const origin = layerRef.current.getBoundingClientRect();
      const rel = (r) => ({
        x: r.left - origin.left + r.width / 2,
        y: r.top - origin.top + r.height / 2,
        r: { left: r.left - origin.left, top: r.top - origin.top, right: r.right - origin.left, bottom: r.bottom - origin.top, width: r.width, height: r.height },
      });

      const tw = rel(twitch.getBoundingClientRect());
      const tk = rel(tiktok.getBoundingClientRect());
      const lg = rel(logoClick.getBoundingClientRect());
      const lr = { top: l.top - origin.top, bottom: l.bottom - origin.top };

      const startY = lr.bottom + 60;
      const waypoints = [
        { x: -90, y: startY },
        { x: tw.r.left - 60, y: tw.r.top + tw.r.height * 0.55 },
        { x: tw.x - tw.r.width * 0.12, y: tw.r.top + 46 }, // CLICK 1 (Twitch)
        { x: (tw.r.right + tk.r.left) / 2, y: tw.r.top - 40 },
        { x: tk.x - tk.r.width * 0.12, y: tk.r.top + 46 }, // CLICK 2 (TikTok)
        { x: tk.r.right + 90, y: tk.r.top + tk.r.height * 0.9 },
        { x: tk.r.right + 40, y: (tk.r.bottom + lr.top) / 2 },
        { x: (tw.r.left + tw.r.right) / 2, y: (tw.r.bottom + lr.top) / 2 + 18 },
        { x: lg.x, y: lg.y }, // CLICK 3 (a logo)
        { x: -90, y: startY },
      ];
      const clicks = [waypoints[2], waypoints[4], waypoints[8]];

      const d = splinePath(waypoints);
      const fr = clickFractions(d, clicks).sort((a, b) => a - b);
      const name = `dtlhand${Date.now().toString(36)}`;
      const css = buildKeyframes(name, fr);
      const trails = Array.from({ length: TRAIL_COUNT }, () => (Math.random() > 0.5 ? "1" : "0"));

      setAnim({ d, name, css, trails });
    };
    raf = requestAnimationFrame(check);
    return () => cancelAnimationFrame(raf);
  }, []);

  const base = anim
    ? { position: "absolute", left: 0, top: 0, offsetPath: `path("${anim.d}")`, willChange: "offset-distance" }
    : {};

  return (
    <div
      ref={layerRef}
      className="pointer-events-none absolute inset-0 z-30 overflow-visible"
      aria-hidden
    >
      {anim && (
        <div data-testid="hand-guide">
          <style>{anim.css}</style>

          {/* 0/1 particle trail (behind the hand) */}
          {anim.trails.map((ch, i) => (
            <span
              key={i}
              style={{
                ...base,
                offsetRotate: "0deg",
                animation: `${anim.name} ${DURATION}s linear both`,
                animationDelay: `${(i + 1) * 0.045}s`,
                color: "#00ff88",
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: `${13 - i * 0.4}px`,
                opacity: Math.max(0.05, 0.42 * (1 - i / TRAIL_COUNT)),
                textShadow: "0 0 6px rgba(0,255,136,0.6)",
              }}
            >
              {ch}
            </span>
          ))}

          {/* Faint depth ghost of the hand */}
          <div
            className="hand-wire-ghost"
            style={{
              ...base,
              offsetRotate: "auto",
              animation: `${anim.name} ${DURATION}s linear both`,
              animationDelay: "0.05s",
              opacity: 0.25,
              transform: "scale(1.25)",
              filter: "drop-shadow(0 0 14px rgba(0,255,136,0.5))",
            }}
          >
            <Pointer size={52} strokeWidth={1} />
          </div>

          {/* Main dotted hand */}
          <div
            className="hand-wire"
            style={{
              ...base,
              offsetRotate: "auto",
              animation: `${anim.name} ${DURATION}s linear both`,
              opacity: 0.7,
              filter: "drop-shadow(0 0 10px rgba(0,255,136,0.7))",
            }}
            onAnimationEnd={() => {
              playing.current = false;
              setAnim(null);
            }}
          >
            <Pointer size={44} strokeWidth={1.1} />
          </div>
        </div>
      )}
    </div>
  );
};
